import { X, Download, Share2, Chrome } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { usePWA } from '@/hooks/usePWA';
import { useEffect, useState } from 'react';
import { getPlatformInfo, getInstallInstructions } from '@/utils/platform';
import { useLocation } from '@tanstack/react-router';

/**
 * Get the PWA install re-prompt delay in hours from environment variable
 * Defaults to 168 hours (7 days) if not set or invalid
 */
function getRepromptDelayHours(): number {
  const envValue = import.meta.env.VITE_PWA_INSTALL_REPROMPT_HOURS;
  const parsed = parseFloat(envValue);

  if (isNaN(parsed) || parsed < 0) {
    return 168; // 7 days default
  }

  return parsed;
}

export function PWAInstallPrompt() {
  const {
    shouldShowInstallPrompt,
    promptInstall,
    dismissInstallPrompt,
    canInstall,
  } = usePWA();
  const [isVisible, setIsVisible] = useState(false);
  const [platform] = useState(() => getPlatformInfo());
  const [instructions] = useState(() => getInstallInstructions(platform));
  const location = useLocation();

  useEffect(() => {
    // Don't show if already installed
    if (platform.isStandalone) {
      return;
    }

    // Only show on dashboard routes (not during onboarding)
    const isDashboard = location.pathname.startsWith('/dashboard');
    if (!isDashboard) {
      setIsVisible(false);
      return;
    }

    // Developer mode: Force show install instructions
    const forceShow = localStorage.getItem('pwa-force-install') === 'true';

    // Check if we should show based on platform
    const shouldShow =
      forceShow ||
      (platform.supportsInstallPrompt
        ? shouldShowInstallPrompt // Android Chrome/Edge - use native event
        : !platform.isStandalone); // iOS/Firefox - show manual instructions

    // Check dismissal for manual prompts (unless forced)
    if (!forceShow && !platform.supportsInstallPrompt) {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (dismissed) {
        const dismissedDate = new Date(dismissed);
        const hoursSinceDismissed =
          (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60);
        const repromptDelayHours = getRepromptDelayHours();

        if (hoursSinceDismissed < repromptDelayHours) {
          return;
        }
      }
    }

    if (shouldShow) {
      const delay = forceShow ? 500 : 2000;
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [shouldShowInstallPrompt, platform, location.pathname, canInstall]);

  if (!isVisible || platform.isStandalone) {
    return null;
  }

  const handleInstall = async () => {
    if (instructions.canShowCustomPrompt) {
      const installed = await promptInstall();
      if (installed) {
        setIsVisible(false);
      }
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    dismissInstallPrompt();
  };

  // Get appropriate icon
  const Icon =
    platform.isIOS && platform.browser === 'safari'
      ? Share2
      : platform.isIOS && platform.browser === 'chrome'
        ? Chrome
        : Download;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '4px',
        left: '4px',
        right: '4px',
        zIndex: 99999,
        maxWidth: 'md',
      }}
      className='fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md'
    >
      <Card
        style={{
          border: '2px solid #00000020',
          backgroundColor: '#ffffff95',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 10px 0 #00000020',
        }}
        className='border-2 border-primary/20 bg-background/95 backdrop-blur-sm shadow-lg'
      >
        <div className='flex items-start gap-3 p-4'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10'>
            <Icon className='h-5 w-5 text-primary' />
          </div>

          <div className='flex-1 space-y-1'>
            <h3 className='font-semibold'>{instructions.title}</h3>

            {/* Show different content based on platform */}
            {instructions.canShowCustomPrompt ? (
              // Android Chrome/Edge with native prompt
              <>
                <p className='text-sm text-muted-foreground'>
                  Install our app for a better experience with offline support
                  and quick access from your home screen.
                </p>
                <div className='flex gap-2 pt-2'>
                  <Button onClick={handleInstall} size='sm' className='flex-1'>
                    Install
                  </Button>
                  <Button onClick={handleDismiss} size='sm' variant='outline'>
                    Not now
                  </Button>
                </div>
              </>
            ) : (
              // iOS Safari or other browsers - show manual instructions
              <>
                <div className='text-sm space-y-1'>
                  <p className='text-muted-foreground mb-2'>
                    Install Polybank for offline access and a native app
                    experience:
                  </p>
                  <ol className='list-decimal list-inside space-y-1 text-muted-foreground'>
                    {instructions.instructions.map((instruction, i) => (
                      <li key={i} className='text-xs'>
                        {instruction}
                      </li>
                    ))}
                  </ol>
                </div>
                <div className='flex gap-2 pt-2'>
                  <Button
                    onClick={handleDismiss}
                    size='sm'
                    variant='outline'
                    className='flex-1'
                  >
                    Got it
                  </Button>
                </div>
              </>
            )}
          </div>

          <Button
            onClick={handleDismiss}
            size='icon'
            variant='ghost'
            className='h-8 w-8 shrink-0'
            aria-label='Dismiss install prompt'
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      </Card>
    </div>
  );
}
